import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

const TOAST_TYPES = {
  success: { bg: '#d4edda', color: '#155724', border: '#c3e6cb', icon: '\u2713' },
  error:   { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb', icon: '\u2717' },
  warning: { bg: '#fff3cd', color: '#856404', border: '#ffeeba', icon: '\u26A0' },
  info:    { bg: '#d1ecf1', color: '#0c5460', border: '#bee5eb', icon: '\u2139' },
};

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 4000;

let toastIdCounter = 0;

const ToastItem = ({ toast, onClose }) => {
  const [exiting, setExiting] = useState(false);
  const style = TOAST_TYPES[toast.type] || TOAST_TYPES.info;

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  }, [onClose, toast.id]);

  useEffect(() => {
    if (toast.duration === 0) return;
    const timer = setTimeout(handleClose, toast.duration || DEFAULT_DURATION);
    return () => clearTimeout(timer);
  }, [handleClose, toast.duration]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 16px',
        marginBottom: '8px',
        borderRadius: '8px',
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '300px',
        maxWidth: '420px',
        fontSize: '14px',
        lineHeight: '1.4',
        animation: exiting ? 'toastSlideOut 0.3s ease-in forwards' : 'toastSlideIn 0.3s ease-out forwards',
        pointerEvents: 'auto',
      }}
    >
      <span style={{ fontWeight: 'bold', fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>
        {style.icon}
      </span>
      <span style={{ flex: 1, wordBreak: 'break-word' }}>{toast.message}</span>
      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          color: style.color,
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: '1',
          padding: '0 2px',
          opacity: 0.7,
          flexShrink: 0,
        }}
        aria-label="Kapat"
      >
        &times;
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((type, message, duration) => {
    const id = ++toastIdCounter;
    setToasts(prev => {
      const next = [...prev, { id, type, message, duration: duration ?? DEFAULT_DURATION }];
      if (next.length > MAX_TOASTS) {
        return next.slice(next.length - MAX_TOASTS);
      }
      return next;
    });
    return id;
  }, []);

  const toast = useRef({
    success: (msg, duration) => addToast('success', msg, duration),
    error: (msg, duration) => addToast('error', msg, duration),
    warning: (msg, duration) => addToast('warning', msg, duration),
    info: (msg, duration) => addToast('info', msg, duration),
  });

  // Keep refs updated
  useEffect(() => {
    toast.current.success = (msg, duration) => addToast('success', msg, duration);
    toast.current.error = (msg, duration) => addToast('error', msg, duration);
    toast.current.warning = (msg, duration) => addToast('warning', msg, duration);
    toast.current.info = (msg, duration) => addToast('info', msg, duration);
  }, [addToast]);

  return (
    <ToastContext.Provider value={toast.current}>
      {children}
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes toastSlideOut {
          from { transform: translateX(0);    opacity: 1; }
          to   { transform: translateX(100%); opacity: 0; }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onClose={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};
